import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg, inr, dt, customerStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteChrome";
import { FarePreview } from "@/components/FarePreview";
import { StatusBadge } from "@/components/Brand";
import { inputCls } from "@/pages/Auth";

export function BookReview() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state] = useState(() => JSON.parse(sessionStorage.getItem("nf_booking") || "null"));
  const [quote, setQuote] = useState(state?.quote);
  const [sel, setSel] = useState(state?.rate_class);
  const [c, setC] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "" });
  const [x, setX] = useState({ instructions: "", flight_number: "", flight_time: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!state) return <div className="p-16" data-testid="book-empty">No fare selected. <Link to="/" className="font-semibold underline">Start a booking</Link></div>;
  const st = state.req.service_type;
  const opt = quote.options.find((o) => o.rate_class === sel);
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const { raw, ...req } = state.req;
    try {
      const { data } = await api.post("/bookings", { ...req, ...x, rate_class: sel, customer: c, idempotency_key: state.key,
        expected_total_paise: opt?.fare_status === "confirmation_required" ? null : opt?.total_paise });
      sessionStorage.removeItem("nf_booking");
      nav(`/booking/${data.booking_id}/confirmation`, { state: { booking: data } });
    } catch (e2) {
      setErr(errMsg(e2));
      if (e2.response?.status === 409) {
        const { data } = await api.post("/quotes", req).catch(() => ({ data: null }));
        if (data) setQuote(data);
      }
    } finally { setBusy(false); }
  };
  const s = (o, set, k) => (ev) => set({ ...o, [k]: ev.target.value });
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <main className="mx-auto grid max-w-6xl gap-8 px-5 py-12 lg:grid-cols-5">
        <form onSubmit={confirm} className="space-y-5 rounded-2xl border bg-white p-7 lg:col-span-3" data-testid="booking-review-form">
          <h1 className="text-2xl font-bold text-[#0A192F]">Review & confirm</h1>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={inputCls} placeholder="Passenger name" value={c.name} onChange={s(c, setC, "name")} required data-testid="customer-name-input" />
            <input className={inputCls} placeholder="Mobile number" value={c.phone} onChange={s(c, setC, "phone")} required data-testid="customer-phone-input" />
            <input className={`${inputCls} sm:col-span-2`} type="email" placeholder="Email" value={c.email} onChange={s(c, setC, "email")} required data-testid="customer-email-input" />
          </div>
          {st.startsWith("airport") && (
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inputCls} placeholder={st === "airport_pickup" ? "Flight number (required)" : "Flight number (optional)"} value={x.flight_number} onChange={s(x, setX, "flight_number")} required={st === "airport_pickup"} data-testid="flight-number-input" />
              <label className="text-xs text-slate-500">{st === "airport_pickup" ? "Scheduled arrival" : "Flight departure"}
                <input type="datetime-local" className={inputCls} value={x.flight_time} onChange={s(x, setX, "flight_time")} data-testid="flight-time-input" /></label>
            </div>
          )}
          <textarea className={inputCls} rows={3} maxLength={500} placeholder="Additional instructions (optional)" value={x.instructions} onChange={s(x, setX, "instructions")} data-testid="instructions-input" />
          {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="booking-error">{err}</p>}
          <button type="submit" disabled={busy || !opt} data-testid="confirm-booking-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-4 font-bold text-[#0A192F] transition-transform hover:-translate-y-0.5 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Booking
          </button>
          <p className="text-xs text-slate-500">By confirming you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/refund-policy" className="underline">Cancellation & Refund Policy</Link>. No payment is taken now.</p>
        </form>
        <aside className="rounded-2xl border bg-white p-7 lg:col-span-2"><FarePreview quote={quote} selected={sel} onSelect={setSel} /></aside>
      </main>
    </div>
  );
}

export function BookingConfirmation() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/me/bookings/${id}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [id]);
  if (b === null) return <div className="p-16">Loading…</div>;
  if (!b) return <div className="p-16">Booking not found.</div>;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 py-16">
        <div className="rounded-2xl border bg-white p-8 nf-rise" data-testid="booking-confirmation">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" />
          <h1 className="mt-4 text-2xl font-bold text-[#0A192F]" data-testid="confirmation-heading">Your booking request has been received successfully!</h1>
          <p className="mt-3 leading-relaxed text-slate-600">Thank you for choosing Nammaafleet. Our operations team will assign an eligible driver and vehicle shortly. You will receive the driver and vehicle details through WhatsApp and email once the assignment is confirmed.</p>
          <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
            {[["Booking ID", b.booking_id], ["Trip type", b.service_label], ["Pickup", b.pickup.address], ["Destination", b.drop?.address || "As per package"],
              ["Pickup date & time", dt(b.pickup_at_utc)], ["Category", `${b.category} · ${b.rate_class}`],
              ["Fare", b.fare.total_paise != null ? `${inr(b.fare.total_paise)}${b.fare.fare_status !== "fixed" ? " (estimated)" : ""}` : "To be confirmed by operations"]].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-1 font-semibold text-[#0A192F]" data-testid={`confirmation-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd></div>
            ))}
            <div><dt className="text-xs uppercase tracking-wider text-slate-500">Status</dt><dd className="mt-1"><StatusBadge value={customerStatus(b.status)} testid="confirmation-status" /></dd></div>
          </dl>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/account/bookings/${b.booking_id}`} className="rounded-full bg-[#0A192F] px-5 py-2.5 text-sm font-semibold text-white" data-testid="view-booking-btn">View My Booking</Link>
            <Link to="/" className="rounded-full border px-5 py-2.5 text-sm font-semibold" data-testid="back-home-btn">Back to Home</Link>
            <Link to="/contact" className="rounded-full border px-5 py-2.5 text-sm font-semibold" data-testid="contact-support-btn">Contact Support</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
